import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { CheckCircle, Phone, MessageCircle } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { CTABlock } from "@/components/CTABlock";
import { PrimaryCTA } from "@/components/PrimaryCTA";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { seoHead, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";

export const Route = createFileRoute("/about")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) => seoHead({
    path: "/about",
    title: "About Woodseer Autos | Independent Garage East London E14",
    description: "Independent garage in Poplar, East London. Honest mechanics, clear advice, fair pricing. MOT, servicing, diagnostics, repairs and bodywork.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "About", path: "/about" },
    ],
    jsonLd: [localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance))],
  }),
  component: AboutPage,
});

function AboutPage() {
  const biz = getBusinessConfig();

  return (
    <>
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground tracking-tight text-center">About Woodseer Autos</h1>
          <div className="mt-8 space-y-5 text-muted-foreground leading-relaxed">
            <p>
              Woodseer Autos is an independent garage based in East London, serving drivers across Poplar, Canary Wharf, Isle of Dogs, Limehouse and the wider Tower Hamlets area.
            </p>
            <p>
              We offer the full range of garage services — <Link to="/mot" className="text-primary hover:text-primary/80">MOT testing</Link>, <Link to="/servicing" className="text-primary hover:text-primary/80">car servicing</Link>, <Link to="/diagnostics" className="text-primary hover:text-primary/80">engine diagnostics</Link>, <Link to="/mechanical-repairs" className="text-primary hover:text-primary/80">mechanical repairs</Link> and <Link to="/bodywork" className="text-primary hover:text-primary/80">bodywork</Link> — all handled by experienced technicians who take genuine pride in their work.
            </p>
            <p>
              We're not a franchise. We're not a dealer. We're a proper local garage that treats every customer the way we'd want to be treated: with honesty, clear communication and fair pricing. No jargon, no upselling, no unnecessary work.
            </p>
            <p>
              Whether you need a routine MOT, a full service, a diagnostic check on a warning light, or a quote for bodywork — you'll get straight answers and quality work from a team that actually cares about doing the job right.
            </p>
          </div>

          {/* Quick contact */}
          <div className="mt-10 flex justify-center">
            <PrimaryCTA location="about-hero" intent="quote or callback" variant="light" />
          </div>
        </div>
      </section>

      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">What We Stand For</h2>
            <StaggerContainer className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: "Honesty First", desc: "We'll always tell you what your vehicle actually needs — and what it doesn't. If it's not worth fixing, we'll say so." },
                { title: "Clear Communication", desc: "No mechanic-speak. We explain findings in plain English so you can make informed decisions about your vehicle." },
                { title: "Fair Pricing", desc: "You'll get an itemised quote before we start. No surprise charges, no hidden fees." },
                { title: "Quality Work", desc: "We use quality parts, follow proper procedures and don't cut corners. Your vehicle leaves our garage in better shape than it arrived." },
                { title: "Respect for Your Time", desc: "We give realistic timeframes and keep you updated. If anything changes, you'll hear from us before it becomes a problem." },
                { title: "Local Commitment", desc: "We're part of this community. Our reputation depends on doing right by the people who live and work nearby." },
              ].map((item) => (
                <StaggerItem key={item.title}>
                  <div className="flex gap-4 p-5 rounded-xl border border-border/50">
                    <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-foreground">{item.title}</h3>
                      <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Who we work with */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="text-3xl font-bold text-foreground">We Work on All Vehicles</h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              From daily commuters and family cars to vans, executive saloons and performance motors — every vehicle gets the same level of care, attention and honest advice.
            </p>
          </div>
        </section>
      </AnimatedSection>

      <CTABlock headline="Ready to Talk?" subtext="Give us a call or pop in. We're always happy to help — no pressure, just honest advice." />
    </>
  );
}
